import React, { useEffect, useState } from 'react';
import { api, apiErrorMessage } from '../api/client';

interface Stats {
  enrolled_students: number;
  fully_compliant: number;
  fully_compliant_pct: number;
  pending_review: number;
  non_compliant: number;
  exemptions: number;
  vaccine_breakdown: Record<string, number>;
}

const Card: React.FC<{ label: string; value: string; sub?: string }> = ({
  label,
  value,
  sub,
}) => (
  <div className="rounded-xl bg-white p-4 shadow-sm">
    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
    {sub && <div className="text-xs text-slate-500">{sub}</div>}
  </div>
);

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/api/admin/dashboard/stats')
      .then((res) => setStats(res.data.stats as Stats))
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load stats.')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-500">Loading dashboard…</div>;
  if (error) return <div className="text-red-600">{error}</div>;
  if (!stats) return null;

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-900">
        County Overview &amp; Compliance Stats
      </h1>

      <div
        role="status"
        className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      >
        <strong>Florida DOH Rule 64D-3.046 Governance Notice:</strong> Only nurse-verified doses
        and staff-recorded exemptions (DH 680 Part B/C, DH 681, or 30-day statutory waivers)
        produce authoritative compliance status. Verify every student record against the
        uploaded document or Florida SHOTS before certifying a DH 680 or submitting DH 684 /
        FLDOE reports.
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card label="Enrolled Students" value={String(stats.enrolled_students)} />
        <Card
          label="Fully Compliant"
          value={String(stats.fully_compliant)}
          sub={`${stats.fully_compliant_pct}%`}
        />
        <Card label="Pending Review" value={String(stats.pending_review)} />
        <Card label="Exemptions" value={String(stats.exemptions)} />
      </div>

      <div className="rounded-xl bg-white p-5 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">
          Florida DH 680 Compliance by Vaccine Group
        </h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {Object.entries(stats.vaccine_breakdown).map(([group, pct]) => (
            <div key={group} className="rounded-lg bg-slate-50 p-3 text-center">
              <div className="text-sm font-medium text-slate-700">{group}</div>
              <div className="text-lg font-semibold text-blue-600">{pct}%</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl bg-white p-5 shadow-sm">
        <p className="text-sm text-slate-600">
          {stats.non_compliant} student{stats.non_compliant === 1 ? '' : 's'} currently
          non-compliant. Use the{' '}
          <a href="/directory" className="font-medium text-blue-600 hover:underline">
            Student Directory
          </a>{' '}
          to review and act on individual records.
        </p>
      </div>
    </div>
  );
};

export default DashboardPage;
